import {
  findRewardBenefitFromHydratedCartEntry,
  getCartEntryId,
  isDummyItem,
} from 'features/cart/utils/minimal-cart';

import { ILegacyCartEntry, ISanityImage } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useGetMenuObjectQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-query';
import { IMenuObject } from 'state/menu/types';
import { isCartEntryComboSlot, isCartEntryItemOption } from 'utils/cart/is-cart-type';
import { CartEntryType, ComboSlotUiPattern } from 'utils/cart/types';
import { maybeMapCartEntryToEntryTypeAndIdForDisplay } from 'utils/menu/cart-entries-to-menu-object-id';
import { IMenuObjectDisplayData, getMenuObjectNameMap } from 'utils/menu/get-menu-object-name-map';

export interface ModifierMultiplierMap {
  id: string;
  modifierMultiplier: number;
}

export type ComposedCartItemType = Pick<ILegacyCartEntry, 'name' | 'price' | 'image' | 'type'> & {
  calories?: number;
  modifierMultiplier?: number;
  children?: ComposedCartItemType[];
  modifiers?: ComposedCartItemType[];
};

export interface CartEntryMappingParams {
  cartEntry: ILegacyCartEntry;
  hiddenCartEntryIds: Set<string>;
}

const HIDDEN_UI_PATTERNS = [ComboSlotUiPattern.HIDDEN, ComboSlotUiPattern.COLLAPSED];

/**
 * Sorts the modifiers of a cart entry by their modifier multiplier in descending order.
 * @returns Composed cart item with sorted modifiers
 */
const sortComposedCartItemChildrenByModifierMultiplier = (
  composedCartItem: ComposedCartItemType
): ComposedCartItemType => {
  return {
    ...composedCartItem,
    modifiers: composedCartItem.modifiers?.sort(
      (a, b) => (b.modifierMultiplier ?? 0) - (a.modifierMultiplier ?? 0)
    ),
  };
};

/**
 * Compiles a list of hidden cart entry IDs from a cart entry.
 * @returns {string[]} Sanity IDs of hidden cart entries
 */
const getHiddenSanityIdsFromCartEntry = (cartEntry?: ILegacyCartEntry): string[] => {
  return (cartEntry?.children ?? [])
    .filter(child => child.uiPattern && HIDDEN_UI_PATTERNS.includes(child.uiPattern))
    .map(child => child._id);
};

/**
 * Compiles a list of hidden cart entry IDs from a menu object.
 * @returns {string[]} Sanity IDs of hidden cart entries
 */
const getHiddenSanityIdsFromMenuObject = (menuObject: IMenuObject | null | undefined): string[] => {
  const hiddenCartEntryIds: string[] = [];
  for (const option of menuObject?.options ?? []) {
    if (!option) {
      continue;
    }

    if (
      'uiPattern' in option &&
      HIDDEN_UI_PATTERNS.includes(option.uiPattern as ComboSlotUiPattern)
    ) {
      hiddenCartEntryIds.push(option._id);
    }
  }

  return hiddenCartEntryIds;
};

/**
 * Recursively maps a cart entry to a composed cart item.
 *
 * `freshDisplay` carries the current, correctly-localized name/image for the TOP-LEVEL
 * entry only (resolved once per `useComposeCartItemUIData` call from `useGetMenuObjectQuery`,
 * matching only the top-level cart entry's sanityId). Cart entry name/image are baked in as
 * plain strings at add-to-cart time and persisted verbatim; nothing re-derives them on a
 * later language change, so the cart page would otherwise keep showing whatever language was
 * active when the item was added. It must not be applied to recursive child calls, since
 * those children have their own distinct sanity IDs that `freshDisplay` doesn't correspond to.
 * @returns {ComposedCartItemType} Composed cart item
 */
const getCartEntryChildrenMapForItem = ({
  cartEntry,
  hiddenCartEntryIds,
  freshDisplay,
  childDisplayDataMap,
}: CartEntryMappingParams & {
  freshDisplay?: { name?: string; image?: ISanityImage };
  childDisplayDataMap?: Map<string, IMenuObjectDisplayData>;
}): ComposedCartItemType => {
  if (
    hiddenCartEntryIds.has(cartEntry.sanityId) ||
    cartEntry.isDummyItem ||
    isDummyItem(cartEntry) ||
    isCartEntryComboSlot(cartEntry) ||
    isCartEntryItemOption(cartEntry)
  ) {
    for (const child of cartEntry.children) {
      return getCartEntryChildrenMapForItem({
        cartEntry: child,
        hiddenCartEntryIds,
        childDisplayDataMap,
      });
    }
  }

  const modifiers: ComposedCartItemType[] = cartEntry.children.flatMap(child => {
    const childrenToMap = child.children.length > 0 ? child.children : [child];
    return childrenToMap.map(grandChild =>
      getCartEntryChildrenMapForItem({
        cartEntry: grandChild,
        hiddenCartEntryIds,
        childDisplayDataMap,
      })
    );
  });

  // A rehydrated cart entry's `_id` can end up as a backend/line identifier while `sanityId`
  // keeps the real Sanity id/key that getMenuObjectNameMap is keyed by — try `sanityId` first,
  // matching the established getCartEntryId precedence used elsewhere in the codebase for this
  // same concern (e.g. use-reprice-cart-entries.ts), so a hydrated child isn't missed here.
  const childDisplayData = childDisplayDataMap?.get(getCartEntryId(cartEntry));

  const result = {
    calories: cartEntry.nutritionWithModifiers?.calories,
    image: freshDisplay?.image ?? childDisplayData?.image ?? cartEntry.image,
    modifierMultiplier: cartEntry.modifierMultiplier?.multiplier,
    name: freshDisplay?.name ?? childDisplayData?.name ?? cartEntry.name,
    price: cartEntry.price,
    type: cartEntry.type,
    modifiers,
  };

  return sortComposedCartItemChildrenByModifierMultiplier(result);
};

/**
 * Recursively maps a combo cart entry to a composed cart item.
 *
 * `freshDisplay` only applies at the top level (see `getCartEntryChildrenMapForItem`), but a
 * combo's nested cart entries (combo slots, their selected items, item options, modifiers) are
 * just as stale and are covered instead by `childDisplayDataMap` — a fresh id/key -> { name,
 * image } lookup built from the combo's own `menuObject`, which already contains the entire
 * nested tree.
 * @returns {ComposedCartItemType} Composed cart item
 */
const getCartEntryChildrenMapForCombo = ({
  cartEntry,
  hiddenCartEntryIds,
  freshDisplay,
  childDisplayDataMap,
}: CartEntryMappingParams & {
  freshDisplay?: { name?: string; image?: ISanityImage };
  childDisplayDataMap?: Map<string, IMenuObjectDisplayData>;
}): ComposedCartItemType => {
  const { children } = cartEntry;

  const rewardBenefit = findRewardBenefitFromHydratedCartEntry(cartEntry);
  if (rewardBenefit) {
    return getCartEntryChildrenMapForItem({
      cartEntry: {
        ...rewardBenefit,
        image: rewardBenefit.image ?? cartEntry.image,
      },
      hiddenCartEntryIds,
    });
  }

  const result = {
    image: freshDisplay?.image ?? cartEntry.image,
    name: freshDisplay?.name ?? cartEntry.name,
    price: cartEntry.price,
    type: cartEntry.type,
    children: children
      .filter(
        child => !hiddenCartEntryIds.has(child._id) && !child.isDummyItem && !isDummyItem(child)
      )
      .map(child =>
        getCartEntryChildrenMapForItem({
          cartEntry: child,
          hiddenCartEntryIds,
          childDisplayDataMap,
        })
      ),
  };

  return sortComposedCartItemChildrenByModifierMultiplier(result);
};

export const useComposeCartItemUIData = (
  cartEntry: ILegacyCartEntry
): ComposedCartItemType | null => {
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { sanityId, menuObjectType } = maybeMapCartEntryToEntryTypeAndIdForDisplay(cartEntry);
  const legacySanityId = isUnifiedMenuEnabled ? '' : sanityId;

  const { data: menuObject } = useGetMenuObjectQuery({
    sanityId: legacySanityId,
    menuObjectType: menuObjectType as MenuObjectTypes,
  });

  const hiddenCartEntryIds = new Set([
    ...getHiddenSanityIdsFromCartEntry(cartEntry),
    ...(!isUnifiedMenuEnabled ? getHiddenSanityIdsFromMenuObject(menuObject) : []),
  ]);

  // Cart entry name/image are baked in as plain strings at add-to-cart time and persisted
  // verbatim to storage; nothing re-derives them on a later language change. `menuObject`
  // above is a fresh query (already correctly localized for the current language), so
  // prefer its name/image for display — only when it actually resolved a value, so a
  // lookup miss (e.g. a discontinued item) can't blank out a name that was already correct.
  // `image.asset` is nullable at the network level even when `image` itself is present
  // (IMenuImageFragment), so a resolved-but-asset-less image must not replace an already-valid
  // persisted image with an unusable reference.
  const freshImage = menuObject?.image as ISanityImage | undefined;
  const freshDisplay =
    !isUnifiedMenuEnabled && menuObject
      ? {
          name: menuObject.name?.locale ?? undefined,
          image: freshImage?.asset?._id ? freshImage : undefined,
        }
      : undefined;

  // Fresh, currently-localized display data (name/image) for the combo's nested tree (combo
  // slots, selected items, item options, modifiers) — see getMenuObjectNameMap. Legacy-only,
  // same as freshDisplay.
  const childDisplayDataMap = !isUnifiedMenuEnabled ? getMenuObjectNameMap(menuObject) : undefined;

  switch (cartEntry.type) {
    case CartEntryType.item:
      return getCartEntryChildrenMapForItem({
        cartEntry,
        hiddenCartEntryIds,
        freshDisplay,
        childDisplayDataMap,
      });
    case CartEntryType.offerCombo:
    case CartEntryType.combo:
      return getCartEntryChildrenMapForCombo({
        cartEntry,
        hiddenCartEntryIds,
        freshDisplay,
        childDisplayDataMap,
      });
    default:
      return {
        name: freshDisplay?.name ?? cartEntry.name,
        price: cartEntry.price,
        image: freshDisplay?.image ?? cartEntry.image,
        type: cartEntry.type,
        children: [],
      };
  }
};
